import {
  getConflictsByRegion,
  categoryLabel,
  categoryColor,
  formatDeaths,
} from '../utils';
import type { Region, ConflictCategory } from '../types';

const categories: ConflictCategory[] = ['major_war', 'minor_war', 'conflict', 'skirmish'];

export function renderRegionsView(container: HTMLElement): void {
  const wrapper = document.createElement('div');
  wrapper.className = 'view-container';
  container.appendChild(wrapper);

  const byRegion = getConflictsByRegion();
  const regions = (Object.keys(byRegion) as Region[]).sort(
    (a, b) => byRegion[b].length - byRegion[a].length
  );

  const maxCount = Math.max(...regions.map(r => byRegion[r].length));
  const barMaxW = 600;

  // Deaths by region
  const deathsByRegion: Record<string, number> = {};
  for (const r of regions) {
    deathsByRegion[r] = byRegion[r].reduce((s, c) => s + c.deaths2025, 0);
  }
  const maxDeaths = Math.max(...Object.values(deathsByRegion));

  wrapper.innerHTML = `
    <div class="view-header">
      <h2>Conflicts by Region</h2>
      <p>Breakdown of active armed conflicts across world regions, stacked by intensity category.</p>
    </div>

    <div class="chart-grid">
      <div class="chart-panel">
        <h3>Conflict Count by Region</h3>
        <p class="chart-subtitle">Horizontal bars stacked by category — ${categories.map(c => `<span style="color:${categoryColor(c)}">${categoryLabel(c)}</span>`).join(' · ')}</p>
        <svg class="chart-svg" viewBox="0 0 800 ${regions.length * 50 + 20}" preserveAspectRatio="xMidYMid meet">
          ${regions.map((r, i) => {
            const y = i * 50 + 10;
            const cs = byRegion[r];

            // Stack segments
            let xPos = 140;
            const segments = categories.map(cat => {
              const count = cs.filter(c => c.category === cat).length;
              const w = (count / maxCount) * barMaxW;
              const seg = { cat, count, x: xPos, w };
              xPos += w;
              return seg;
            });

            return `
              <g>
                <text x="130" y="${y + 20}" text-anchor="end" class="bar-label">${r}</text>
                ${segments.filter(s => s.count > 0).map(s => `
                  <rect class="bar-rect" x="${s.x}" y="${y + 4}" width="${s.w}" height="24" fill="${categoryColor(s.cat)}" rx="2">
                    <title>${r}: ${s.count} ${categoryLabel(s.cat).toLowerCase()}${s.count > 1 ? 's' : ''}</title>
                  </rect>
                `).join('')}
                <text x="${xPos + 8}" y="${y + 20}" class="bar-value">${cs.length}</text>
              </g>
            `;
          }).join('')}
        </svg>
      </div>

      <div class="chart-panel">
        <h3>Estimated Fatalities by Region (2025)</h3>
        <p class="chart-subtitle">Total estimated fatalities across all conflicts in each region</p>
        <svg class="chart-svg" viewBox="0 0 800 ${regions.length * 50 + 20}" preserveAspectRatio="xMidYMid meet">
          ${regions.map((r, i) => {
            const y = i * 50 + 10;
            const deaths = deathsByRegion[r];
            const w = (deaths / maxDeaths) * barMaxW;

            return `
              <g>
                <text x="130" y="${y + 20}" text-anchor="end" class="bar-label">${r}</text>
                <rect class="bar-rect" x="140" y="${y + 4}" width="${w}" height="24" fill="#ef4444" rx="2" opacity="0.7">
                  <title>${r}: ~${formatDeaths(deaths)} fatalities in 2025</title>
                </rect>
                <text x="${140 + w + 8}" y="${y + 20}" class="bar-value">${formatDeaths(deaths)}</text>
              </g>
            `;
          }).join('')}
        </svg>
      </div>
    </div>

    <div style="margin-top: var(--space-2xl);">
      <h3 style="font-size: var(--font-size-lg); margin-bottom: var(--space-lg);">Region Details</h3>
      <div class="chart-grid">
        ${regions.map(r => {
          const cs = byRegion[r];
          const sorted = [...cs].sort((a, b) => b.deaths2025 - a.deaths2025);
          return `
            <div class="chart-panel">
              <h3>${r} — ${cs.length} conflicts</h3>
              <p class="chart-subtitle">~${formatDeaths(deathsByRegion[r])} estimated fatalities in 2025</p>
              <table class="data-table" style="font-size:var(--font-size-xs);">
                <thead><tr><th>Conflict</th><th>Category</th><th>Deaths (2025)</th></tr></thead>
                <tbody>
                  ${sorted.map(c => `
                    <tr>
                      <td>${c.name}</td>
                      <td><span class="category-pill" style="background:${categoryColor(c.category)}20;color:${categoryColor(c.category)}">${categoryLabel(c.category)}</span></td>
                      <td class="mono" style="color:${categoryColor(c.category)}">${formatDeaths(c.deaths2025)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          `;
        }).join('')}
      </div>
    </div>
  `;
}
